import React, {
  memo,
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
  useContext,
} from "react";
import "../../css/DefaultLandingPage.css";
import { useSelector, useDispatch } from "react-redux";
import { LoginState, getAccountState } from "../../redux/slices/accountSlice";
import { Colors } from "../../utils/colors";
import { MARKIT_CONTACT_US, MARKITAI } from "@markit/common.utils";
import { useNavigate } from "../../hooks/useNavigate";
import { DataLoaders } from "../../redux/slices/dataSlice";
import ReachYourFansInstantly from "../../assets/LandingPage/ReachYourFansInstantly.png";
import iMessageOnSteroids from "../../assets/LandingPage/iMessageOnSteroids.png";
import { isDesktop, isMobile } from "react-device-detect";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { Icon } from "@iconify/react";
import {
  LandingPageNavigationId,
  NavigationId,
} from "../../navigation/AppParamList";
import { useSlideUpAnimation } from "../../hooks/animationHooks/useSlideUpAnimation";
import LandingPagesWrapper from "../../components/LandingPage/LandingPagesWrapper";
import { MessageBubble } from "../../components/AnimationContainers/MessageBubble";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import { useWindowResize } from "../../hooks/useWindowResize";
import { MixpanelContext } from "../../context/AnalyticsService";
import CustomerLogoCarousel from "../../components/LandingPage/CustomerLogoCarousel";
import TextingThatFeelsHumanFold from "../../components/LandingPage/TextingThatFeelsHumanFold";
import IntegrateSocialsFold from "../../components/LandingPage/IntegrateSocialsFold";
import MoreThanJustTextingFold from "../../components/LandingPage/MoreThanJustTextingFold";
import ApiIntegrationsFold from "../../components/LandingPage/ApiIntegrationsFold";
import AiToolsFold from "../../components/LandingPage/AiToolsFold";
import RealResultsFold from "../../components/LandingPage/RealResultsFold";
import FAQFold from "../../components/LandingPage/FAQFold";
import TestimonialsFold from "../../components/LandingPage/TestimonialsFold";
import { FoldHeaderText } from "../../components/LandingPage/SubComponents/FoldHeaderText";
import PricingFold from "../../components/LandingPage/PricingFold";

const DefaultLandingPage = memo(function DefaultLandingPageFn() {
  const { account } = useSelector(getAccountState);
  const { accountData, loggedIn } = account;
  const mixpanel = useContext(MixpanelContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useSlideUpAnimation();
  const windowWidth = useWindowResize();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollPosition, setScrollPosition] = useState(0);

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const styles = {
    button: {
      padding: "12px 16px",
      borderRadius: 10,
      width: "fit-content",
    },

    foldContainer: {
      paddingBlock: isDesktopSize ? 80 : 60,
      backgroundImage: "linear-gradient(to bottom, #141516 0%, #000000 31%)",
    },

    mainTitle: {
      fontSize: isDesktopSize ? 80 : 46,
      fontWeight: 500,
      paddingBlock: isDesktopSize ? 24 : 14,
      letterSpacing: "-1px",
      lineHeight: "99.6%",
      backgroundImage: "linear-gradient(to bottom, #FFFFFF 20%, #787878 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    },

    headerLabel: {
      fontSize: isDesktopSize ? 16 : 14,
      fontWeight: 500,
    },

    largeImage: {
      width: "100%",
      height: "auto",
    },

    safeView: {
      paddingInline: 20,
    },
  };

  // Handle mouse movement for interactive effects on desktop
  useEffect(() => {
    if (isMobile) return; // Skip mouse tracking on mobile

    const handleMouseMove = (event: MouseEvent) => {
      setMousePosition({
        x: event.clientX,
        y: event.clientY,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  // Handle scroll position tracking for mobile
  useEffect(() => {
    if (isDesktop) return; // Only track scrolling on mobile

    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Canvas dot grid animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);

    // Create dot grid
    const dotSize = isDesktop ? 1 : 0.7;
    const spacing = isDesktop ? 30 : 20;
    const dots: { x: number; y: number; baseY: number; velocity: number }[] =
      [];

    // Initialize dots
    for (let x = 0; x < canvas.width; x += spacing) {
      for (let y = 0; y < canvas.height; y += spacing) {
        dots.push({
          x,
          y,
          baseY: y,
          velocity: 0.1 + Math.random() * 0.2,
        });
      }
    }

    // Animation loop
    let animationFrameId: number;

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw dots
      dots.forEach((dot) => {
        let opacity = 0.15;

        if (isMobile) {
          // On mobile, use scroll position to determine dot opacity
          // Calculate the dot's position relative to the viewport
          const dotViewportY = dot.y - window.scrollY;
          // Move spotlight focus to the top of the screen instead of center
          // Use 1/4 of the viewport height as the focal point
          const viewportFocus = window.innerHeight / 4;
          const distanceFromFocus = Math.abs(dotViewportY - viewportFocus);
          // Smaller spotlight size - reduce to 1/3 of the viewport height
          const maxDistance = window.innerHeight / 5;

          if (distanceFromFocus < maxDistance) {
            // Enhanced spotlight effect with stronger contrast
            // Use a non-linear curve to create a more dramatic spotlight
            const normalizedDistance = distanceFromFocus / maxDistance;
            // Sharper exponential falloff for more distinct spotlight
            opacity = 0.05 + (1 - Math.pow(normalizedDistance, 2.5)) * 0.75;

            // Add subtle movement based on scroll position
            dot.y =
              dot.baseY +
              Math.sin((scrollPosition + dot.x) * 0.01 * dot.velocity) * 3;
          } else {
            // Make dots outside the spotlight nearly invisible
            opacity = 0.01;
            // Subtle wave effect for dots far from center
            dot.y =
              dot.baseY +
              Math.sin((scrollPosition + dot.x) * 0.01 * dot.velocity) * 1;
          }
        } else {
          // Desktop behavior - react to mouse position
          // Calculate distance from mouse
          const dx = mousePosition.x - dot.x;
          const dy = mousePosition.y - dot.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Interactive effect based on mouse position
          const maxDistance = 300;

          if (distance < maxDistance) {
            opacity = 0.15 + (1 - distance / maxDistance) * 0.75;

            // Subtle movement effect
            dot.y =
              dot.baseY +
              Math.sin(Date.now() * 0.001 * dot.velocity) *
                3 *
                (1 - distance / maxDistance);
          } else {
            // Subtle wave effect for dots far from mouse
            dot.y = dot.baseY + Math.sin(Date.now() * 0.001 * dot.velocity) * 1;
          }
        }

        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dotSize, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrameId = window.requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", updateCanvasSize);
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [mousePosition, scrollPosition]);

  const getStartedOnPress = useCallback(
    (btnSource: string) => {
      if (mixpanel) {
        mixpanel.track("Website: Home Pressed Get Started", {
          distinct_id: accountData.uid,
          is_logged_in: loggedIn === LoginState.LOGGED_IN,
          btn_source: btnSource,
        });
      }
      if (loggedIn === LoginState.LOGGED_IN) {
        dispatch(DataLoaders.user(accountData.uid));
        navigate(`/u/${accountData.username}`, {
          state: { username: accountData.username },
        });
      } else {
        navigate(NavigationId.WELCOME);
      }
    },
    [
      accountData.uid,
      accountData.username,
      dispatch,
      loggedIn,
      navigate,
      mixpanel,
    ]
  );

  const textingFeaturesOnPress = useCallback(
    (btnSource: string) => {
      if (mixpanel) {
        mixpanel.track("Website: Home Pressed Texting", {
          distinct_id: accountData.uid,
          is_logged_in: loggedIn === LoginState.LOGGED_IN,
          btn_source: btnSource,
        });
      }
      navigate(LandingPageNavigationId.TEXTING);
    },
    [accountData.uid, loggedIn, mixpanel, navigate]
  );

  const supportOnPress = useCallback(
    (btnSource: string) => {
      if (mixpanel) {
        mixpanel.track("Website: Home Pressed Contact Us", {
          distinct_id: accountData.uid,
          is_logged_in: loggedIn === LoginState.LOGGED_IN,
          btn_source: btnSource,
        });
      }
      window.open(MARKIT_CONTACT_US);
    },
    [accountData.uid, loggedIn, mixpanel]
  );

  const renderFoldHeaderLabel = useCallback(
    (label: string, iconName: string, color: string) => (
      <div
        className="AlignedRow"
        style={{ gap: 10, paddingBottom: isDesktopSize ? 10 : 7 }}
      >
        <Icon icon={iconName} height={14} style={{ color }} />
        <span style={{ ...styles.headerLabel, color }}>{label}</span>
      </div>
    ),
    [styles.headerLabel, isDesktopSize]
  );

  return (
    <LandingPagesWrapper
      helmetData={{
        title: "Markit AI: Texting for Creators & Brands",
        metaContent:
          "Markit AI is a texting platform that helps creators, brands, and businesses turn followers into text subscribers, publish newsletters, and sell products.",
        link: MARKITAI,
      }}
      preventLogoClick
    >
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{
          paddingTop: 40,
          paddingBottom: isDesktop ? 80 : 60,
          paddingInline: isDesktopSize ? 0 : 20,
          color: Colors.WHITE,
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        />
        {isDesktopSize ? (
          <div
            className="AlignedRow"
            style={{
              paddingBlock: 10,
              paddingInline: 14,
              borderRadius: 100,
              border: `0.5px solid ${Colors.BLACK1}`,
              backgroundColor: Colors.BLACK,
              zIndex: 99,
            }}
          >
            <span className="sectionTitle" style={{ color: Colors.GRAY1 }}>
              <span style={{ color: Colors.BLUE5 }}>1M+</span> texts sent every
              month
            </span>
          </div>
        ) : (
          <div className="AlignedRow" style={{ zIndex: 99 }}>
            <span className="sectionTitle" style={{ color: Colors.GRAY1 }}>
              <span style={{ color: Colors.BLUE5 }}>1M+</span> texts sent every
              month
            </span>
          </div>
        )}
        <h1
          style={{
            textAlign: isDesktopSize ? "center" : "left",
            ...styles.mainTitle,
          }}
        >
          {isDesktopSize ? (
            <>
              Text your audience
              <br />
              and grow your business.
            </>
          ) : (
            <>
              Text your audience
              <br />
              and grow your business.
            </>
          )}
        </h1>
        <span
          className="landingPageHeaderSubtext"
          style={{
            paddingBottom: isDesktopSize ? 30 : 20,
            paddingInline: isDesktopSize ? 20 : 0,
            textAlign: isDesktopSize ? "center" : "left",
            fontSize: isDesktopSize ? 18 : 16,
          }}
        >
          Markit AI is a texting platform for sharing content, built to help
          turn{isDesktopSize ? <br /> : " "}followers into text subscribers,
          publish newsletters, and sell products.
        </span>
        <div
          className={isDesktopSize ? "Centering" : "AlignedRow"}
          style={{
            gap: isDesktopSize ? 14 : 10,
            width: isDesktopSize ? 164 : "auto",
            zIndex: 99,
          }}
        >
          <MessageBubble
            text={
              <div className="AlignedRowSelect" style={{ gap: 4 }}>
                <p className="AlignedRowSelect">Start Texting for Free</p>
                <Icon
                  icon="ion:chevron-forward"
                  height={16}
                  style={{ color: Colors.WHITE }}
                />
              </div>
            }
            tailPosition="bottomLeft"
            onPress={() => getStartedOnPress("Start Texting")}
            containerStyles={{
              borderRadius: 22,
              fontSize: isDesktopSize ? 16 : 12,
              fontWeight: 500,
              padding: "12px 16px",
            }}
          />
          <div className="AlignedRowRight">
            <MessageBubble
              text={"Book a Demo"}
              onPress={() => supportOnPress("Book a Demo")}
              containerStyles={{
                borderRadius: 22,
                fontSize: isDesktopSize ? 16 : 12,
                fontWeight: 500,
                padding: "12px 16px",
              }}
              color={Colors.GRAY7}
            />
          </div>
        </div>
        <div
          style={{
            marginTop: isDesktopSize ? 50 : 24,
            width: "100vw",
            marginLeft: "calc(-50vw + 50%)",
          }}
        >
          <CustomerLogoCarousel isDesktopSize={isDesktopSize} />
        </div>
      </div>
      {/* Fold 2 */}
      <div
        className="AlignedColumnSpaced"
        style={{ ...styles.foldContainer, gap: 60 }}
      >
        <div
          className={isDesktopSize ? "AlignedRowSpaced" : "ColumnNormal"}
          style={{
            maxWidth: 1152,
            gap: isDesktopSize ? 155 : 50,
            ...styles.safeView,
          }}
        >
          <div className="ColumnNormal">
            {renderFoldHeaderLabel(
              "Mass Text Blasting",
              "ion:paper-plane",
              Colors.BLUE5
            )}
            <div className="ColumnNormal" style={{ gap: 40 }}>
              <div className="ColumnNormal" style={{ gap: 24 }}>
                <FoldHeaderText isDesktopSize={isDesktopSize}>
                  Stay top of
                  <br />
                  mind{" "}
                  <span
                    className="AlignedRow"
                    style={{ display: "inline-flex" }}
                  >
                    <span style={{ color: Colors.BLUE5 }}>texting</span>
                    <Icon
                      icon="ion:paper-plane"
                      height={60}
                      style={{
                        color: Colors.BLUE5,
                        marginLeft: 8,
                        transform: "rotate(45deg)",
                      }}
                    />
                  </span>
                </FoldHeaderText>
                <div style={{ width: isDesktopSize ? 360 : "100%" }}>
                  <span className="landingPageHeaderSubtext">
                    <span style={{ color: Colors.WHITE }}>
                      Texts have a 98% open rate.{isDesktopSize ? " " : <br />}
                    </span>
                    Reach people more effectively than email or social media
                    with personalized texts.
                  </span>
                </div>
              </div>
              <RectangleButton
                buttonLabel="Learn More"
                onPress={() => textingFeaturesOnPress("Explore Texting")}
                altColor={Colors.WHITE}
                altTextColor={Colors.BLACK}
                iconRight={
                  <Icon
                    icon="ion:chevron-forward"
                    height={16}
                    style={{ color: Colors.BLACK }}
                  />
                }
                containerStyles={styles.button}
              />
            </div>
          </div>
          <img
            src={ReachYourFansInstantly}
            alt="Markit mass texting interface"
            className="slide-up"
            style={{
              ...styles.largeImage,
              objectFit: "contain",
              maxWidth: 519,
            }}
          />
        </div>
        {/* iMessage on Steroids */}
        <div
          className={isDesktopSize ? "AlignedRow" : "ColumnNormal"}
          style={{
            height: isDesktopSize ? 607 : "auto",
            maxWidth: 1152,
            gap: isDesktopSize ? 153 : 60,
            ...styles.safeView,
          }}
        >
          {isDesktopSize ? (
            <img
              src={iMessageOnSteroids}
              alt="Markit two way conversation interface"
              className="slide-up"
              style={{
                ...styles.largeImage,
                objectFit: "contain",
                maxWidth: 621,
              }}
            />
          ) : null}
          <div className="ColumnNormal" style={{ width: 307 }}>
            {renderFoldHeaderLabel(
              "One to One Texting",
              "ion:chatbubbles",
              Colors.ORANGE1
            )}
            <div className="ColumnNormal" style={{ gap: 24 }}>
              <FoldHeaderText isDesktopSize={isDesktopSize}>
                iMessage
                <br />
                on steroids
              </FoldHeaderText>
              <span className="landingPageHeaderSubtext">
                <span style={{ color: Colors.WHITE }}>
                  Turn followers into customers
                </span>{" "}
                with two-way texting conversations. Build real connections
                through two-way texting.
              </span>
            </div>
          </div>
          {!isDesktopSize ? (
            <img
              src={iMessageOnSteroids}
              alt="Markit two way conversation interface"
              className="slide-up"
              style={{
                ...styles.largeImage,
                objectFit: "contain",
              }}
            />
          ) : null}
        </div>
        {/* Real results */}
        <RealResultsFold isDesktopSize={isDesktopSize} />
      </div>
      {/* Grow text list from Instagram */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{ ...styles.foldContainer, ...styles.safeView }}
      >
        <div style={{ maxWidth: 1152, width: "100%" }}>
          <IntegrateSocialsFold isDesktopSize={isDesktopSize} />
        </div>
      </div>
      {/* AI Tools (MCP) */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{ ...styles.foldContainer, ...styles.safeView }}
      >
        <div style={{ maxWidth: 1152, width: "100%" }}>
          <AiToolsFold isDesktopSize={isDesktopSize} />
        </div>
      </div>
      {/* API Integrations */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{ ...styles.foldContainer, ...styles.safeView }}
      >
        <div style={{ maxWidth: 1152, width: "100%" }}>
          <ApiIntegrationsFold isDesktopSize={isDesktopSize} />
        </div>
      </div>
      {/* More than just texting */}
      <MoreThanJustTextingFold isDesktopSize={isDesktopSize} />
      {/* Testimonials */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{ ...styles.foldContainer, ...styles.safeView }}
      >
        <div style={{ maxWidth: 1152, width: "100%" }}>
          <TestimonialsFold isDesktopSize={isDesktopSize} />
        </div>
      </div>
      {/* Fold 5 - Pricing */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{ ...styles.foldContainer, ...styles.safeView }}
      >
        <div style={{ width: "100%" }}>
          <PricingFold isDesktopSize={isDesktopSize} />
        </div>
      </div>
      {/* FAQ */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{ ...styles.foldContainer, ...styles.safeView }}
      >
        <div style={{ maxWidth: 1152, width: "100%" }}>
          <FAQFold isDesktopSize={isDesktopSize} />
        </div>
      </div>
      {/* Fold 6 */}
      <TextingThatFeelsHumanFold
        isDesktopSize={isDesktopSize}
        getStartedOnPress={getStartedOnPress}
      />
    </LandingPagesWrapper>
  );
});

export default DefaultLandingPage;
